import {
  AlertCircle,
  ChevronDown,
  Eye,
  History,
  Loader2,
  Lock,
  Maximize2,
  Minimize2,
  Redo2,
  Undo2,
} from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/core/components/ui/dropdown-menu";
import { Stack } from "@/core/components/ui/stack";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipShortcut,
  TooltipTrigger,
} from "@/core/components/ui/tooltip";
import { cn } from "@/core/utils/utils";
import type { AiSpreadsheetValueType } from "@/payroll/ai-script/types";
import { CELL_FORMAT_OPTIONS } from "./cell-format-options";
import { VIEWER_OVERLAY_CLASSNAME } from "./utils";

interface FormulaBarProps {
  coordinate: string | null;
  commitTarget: FormulaBarCommitTarget | null;
  value: string;
  errorCode: string | null;
  canEdit: boolean;
  // The blank row under the data is selected — committing adds a row.
  addRowMode?: boolean;
  isSheetReadOnly?: boolean;
  isCellLocked?: boolean;
  isRecalculating: boolean;
  operationError?: string | null;
  onRetryOperation?: () => void;
  isMaximized: boolean;
  rightAccessory?: ReactNode;
  // The selected cell's current value_type — shown as a format indicator in the
  // toolbar. Null when nothing (or a cell without a type) is selected.
  valueType?: string | null;
  // Change the selected cell's format. When provided (and the cell is editable),
  // the format indicator becomes a clickable menu.
  onFormatChange?: (valueType: AiSpreadsheetValueType) => void;
  // When the selected cell carries a persistent edit, a "View edit" segment is
  // shown that opens the manual-edits sidebar focused on that cell.
  onViewEdit?: () => void;
  onCommit: (rawInput: string, target: FormulaBarCommitTarget) => void;
  onToggleMaximize: () => void;
  // Undo/redo controls. Rendered only when both handlers are provided; each
  // button is disabled when there's nothing to act on (or a replay is running).
  // The labels ("Undo edit cell B4") drive the tooltip copy.
  canUndo?: boolean;
  canRedo?: boolean;
  undoLabel?: string | null;
  redoLabel?: string | null;
  // A replay is in flight — disable both controls (the "Calculating…" indicator
  // above already reflects the in-progress mutation).
  isUndoRedoApplying?: boolean;
  onUndo?: () => void;
  onRedo?: () => void;
}

export type FormulaBarCommitTarget =
  | { kind: "cell"; cellId: string }
  | { kind: "new-row"; columnIndex: number };

interface FormulaBarEditSession {
  committed: boolean;
  coordinate: string | null;
  startingValue: string;
  target: FormulaBarCommitTarget;
}

// Formula bar mirroring Excel: cell reference on the left, the cell's raw input
// / formula on the right. When a real cell is selected it is editable and
// commits the same way as in-grid edits (Enter commits, Escape reverts).
export const FormulaBar = ({
  coordinate,
  commitTarget,
  value,
  errorCode,
  canEdit,
  addRowMode = false,
  isSheetReadOnly = false,
  isCellLocked = false,
  isRecalculating,
  operationError,
  onRetryOperation,
  isMaximized,
  rightAccessory,
  valueType,
  onFormatChange,
  onViewEdit,
  onCommit,
  onToggleMaximize,
  canUndo = false,
  canRedo = false,
  undoLabel,
  redoLabel,
  isUndoRedoApplying = false,
  onUndo,
  onRedo,
}: FormulaBarProps) => {
  const [draft, setDraft] = useState(value);
  const currentFormat = CELL_FORMAT_OPTIONS.find(
    (option) => option.value === valueType,
  );
  const editSessionRef = useRef<FormulaBarEditSession | null>(null);

  // Keep an active edit bound to the cell where it started. A grid click can
  // change these props before the input blur event commits the draft.
  useEffect(() => {
    if (!editSessionRef.current) setDraft(value);
  }, [value, coordinate]);

  const commit = () => {
    const editSession = editSessionRef.current;
    if (
      !editSession ||
      editSession.committed ||
      draft === editSession.startingValue
    )
      return;

    editSession.committed = true;
    onCommit(draft, editSession.target);
  };

  return (
    <Stack
      direction="row"
      align="stretch"
      fullWidth
      gap="none"
      className="border-b border-stroke-tertiary bg-surface-primary"
    >
      <Stack
        justify="center"
        gap="none"
        className="w-24 shrink-0 border-r border-stroke-tertiary px-3 text-md font-medium text-secondary-text"
      >
        {coordinate ?? ""}
      </Stack>
      <Stack
        align="center"
        justify="center"
        gap="none"
        className="w-9 shrink-0 border-r border-stroke-tertiary font-serif text-md italic text-tertiary-text"
      >
        fx
      </Stack>
      <input
        value={draft}
        readOnly={!canEdit}
        onFocus={() => {
          editSessionRef.current =
            canEdit && commitTarget
              ? {
                  committed: false,
                  coordinate,
                  startingValue: value,
                  target: commitTarget,
                }
              : null;
        }}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            commit();
            event.currentTarget.blur();
          } else if (event.key === "Escape") {
            const editSession = editSessionRef.current;
            if (editSession) editSession.committed = true;
            setDraft(editSession?.startingValue ?? value);
            event.currentTarget.blur();
          }
        }}
        onBlur={() => {
          const editSession = editSessionRef.current;
          commit();
          editSessionRef.current = null;
          if (editSession?.coordinate !== coordinate) setDraft(value);
        }}
        placeholder={
          addRowMode
            ? "Type a value and press Enter to add a row"
            : canEdit
              ? "Edit this cell's value or formula"
              : isSheetReadOnly
                ? "Select a cell to view its value or formula (view only)"
                : "Select a cell to view its value or formula"
        }
        // No focus ring, matching the search field: the caret is the indicator
        // in a text input, and a 2px box drawn around a field this wide read as
        // chrome rather than as state. The toolbar's icon buttons keep their
        // rings — they have nothing else to show focus with.
        className={cn(
          "min-w-0 flex-1 bg-transparent px-3 py-2 text-md text-primary-text outline-none placeholder:text-tertiary-text",
          { "text-red-50": !!errorCode },
        )}
      />
      {errorCode && (
        <Stack
          justify="center"
          gap="none"
          className="shrink-0 border-l border-stroke-tertiary px-3 text-md font-semibold text-red-50"
        >
          {errorCode}
        </Stack>
      )}
      {coordinate && isCellLocked && (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                tabIndex={0}
                role="img"
                aria-label="Read only cell"
                className="flex shrink-0 items-center justify-center border-l border-stroke-tertiary px-3 text-tertiary-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
              >
                <Lock className="h-4 w-4" aria-hidden="true" />
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom" className={VIEWER_OVERLAY_CLASSNAME}>
              Read only cell
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      {isSheetReadOnly && (
        <Stack
          direction="row"
          align="center"
          gap="xs"
          className="shrink-0 border-l border-stroke-tertiary px-3 text-md text-tertiary-text"
        >
          <Eye className="h-3.5 w-3.5" />
          View only
        </Stack>
      )}
      {isRecalculating && (
        <Stack
          direction="row"
          align="center"
          gap="xs"
          aria-live="polite"
          className="shrink-0 border-l border-stroke-tertiary px-3 text-md text-secondary-text"
        >
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Calculating…
        </Stack>
      )}
      {!isRecalculating && operationError && (
        <Stack
          direction="row"
          align="center"
          gap="xs"
          aria-live="polite"
          className="shrink-0 border-l border-stroke-tertiary px-3 text-md text-red-50"
        >
          <AlertCircle className="h-3.5 w-3.5" />
          <span className="max-w-72 truncate" title={operationError}>
            {operationError}
          </span>
          {onRetryOperation && (
            <button
              type="button"
              onClick={onRetryOperation}
              className="font-semibold underline underline-offset-2"
            >
              Retry
            </button>
          )}
        </Stack>
      )}
      {coordinate && currentFormat && onFormatChange && canEdit ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={`Cell format: ${currentFormat.label}`}
              className="flex shrink-0 items-center gap-1 border-l border-stroke-tertiary px-3 text-md text-tertiary-text transition-colors hover:bg-surface-secondary hover:text-secondary-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
            >
              {currentFormat.icon}
              <ChevronDown className="h-3.5 w-3.5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className={VIEWER_OVERLAY_CLASSNAME}>
            {CELL_FORMAT_OPTIONS.map((option) => (
              <DropdownMenuItem
                key={option.value}
                className="gap-2"
                onSelect={() => onFormatChange(option.value)}
              >
                {option.icon}
                {option.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : coordinate && currentFormat ? (
        <Stack
          direction="row"
          align="center"
          gap="xs"
          className="shrink-0 border-l border-stroke-tertiary px-3 text-md text-tertiary-text"
        >
          {currentFormat.icon}
        </Stack>
      ) : null}
      {onViewEdit && (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={onViewEdit}
                aria-label="View edit"
                className="flex shrink-0 items-center gap-1.5 border-l border-stroke-tertiary px-3 text-md text-tertiary-text transition-colors hover:bg-surface-secondary hover:text-secondary-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
              >
                <History className="h-4 w-4" />
                View edit
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className={VIEWER_OVERLAY_CLASSNAME}>
              View this cell&apos;s edit history
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      {onUndo && onRedo && (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                data-testid="workbook-undo"
                onClick={onUndo}
                disabled={!canUndo || isUndoRedoApplying}
                aria-label={undoLabel ?? "Undo"}
                className="flex shrink-0 items-center justify-center border-l border-stroke-tertiary px-3 text-tertiary-text transition-colors hover:bg-surface-secondary hover:text-secondary-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-tertiary-text"
              >
                <Undo2 className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className={VIEWER_OVERLAY_CLASSNAME}>
              {undoLabel ?? "Nothing to undo"}
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                data-testid="workbook-redo"
                onClick={onRedo}
                disabled={!canRedo || isUndoRedoApplying}
                aria-label={redoLabel ?? "Redo"}
                className="flex shrink-0 items-center justify-center border-l border-stroke-tertiary px-3 text-tertiary-text transition-colors hover:bg-surface-secondary hover:text-secondary-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-tertiary-text"
              >
                <Redo2 className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className={VIEWER_OVERLAY_CLASSNAME}>
              {redoLabel ?? "Nothing to redo"}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      {rightAccessory}
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={onToggleMaximize}
              aria-label={isMaximized ? "Exit full screen" : "Full screen"}
              className="flex shrink-0 items-center justify-center border-l border-stroke-tertiary px-3 text-tertiary-text transition-colors hover:bg-surface-secondary hover:text-secondary-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
            >
              {isMaximized ? (
                <Minimize2 className="h-4 w-4" />
              ) : (
                <Maximize2 className="h-4 w-4" />
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent side="bottom" className={VIEWER_OVERLAY_CLASSNAME}>
            <TooltipShortcut
              keys={isMaximized ? ["Esc"] : ["mod", "shift", "F"]}
            >
              {isMaximized ? "Exit full screen" : "Full screen"}
            </TooltipShortcut>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </Stack>
  );
};
